import { useEffect, useState } from 'react';
import { Alert, Badge, Button, ConfirmDialog, Input, Select, Switch, useToast } from '@dreamtree-org/twreact-ui';
import { Plus, Trash2, Wand2 } from 'lucide-react';
import { SectionCard, QueryState } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import { errorText } from '@/lib/format';
import { useCrudWrite } from '../hooks';
import { isLotLine } from '../lib/orders';
import {
  DEFAULT_LIMITS, STYLES, applyBulk, blankOption, countsOf, generate, hasChanges, labelOf, optionErrors, optionsDraft, optionsOf,
  rowErrors, rowsDraft, savePayload, serverErrors, staleRows,
} from '../lib/variants';

/**
 * Options and variants (MERCH-87, ADR 0009 §3) in the listing editor: a stock-lot listing's
 * options (Size, Colour…; at most 3 of 20 values), the combinations it sells, and per variant
 * its SKU, barcode, price and MRP (blank: the listing's), image (one of the listing's approved
 * photos), stock and whether it is on sale. "Generate combinations" adds the missing rows; bulk
 * price and stock fill every row. Saving sends the changes to ListingVariant.save (catalog:update,
 * all-or-nothing, audited); a counted lot's new stock goes through the audited stock count
 * (InventoryUnit update), as on the stock page. A single-unit listing has one variant.
 */
const NONE = Object.freeze([]);

export default function VariantMatrix({ listingId, canEdit }) {
  const { toast } = useToast();
  const query = useQuery('listingVariants', { listing_id: listingId }, { select: (r) => (r && r.data) || null, enabled: Boolean(listingId) });
  const save = useMutation('saveListingVariants');
  const write = useCrudWrite();
  const m = query.data;
  const limits = (m && m.limits) || DEFAULT_LIMITS;
  const [options, setOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [saved, setSaved] = useState({ options: NONE, rows: NONE });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [bulk, setBulk] = useState({ price: '', qty: '' });
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!m) return;
    const r = rowsDraft(m);
    setOptions(optionsDraft(m.options));
    setRows(r);
    setSaved({ options: m.options || NONE, rows: r });
    setErrors({});
    setFormError('');
  }, [m]);

  const opts = optionsOf(options);
  const stale = staleRows(rows, opts);
  const { request, keys } = savePayload({ listingId, options, savedOptions: saved.options, rows, savedRows: saved.rows });
  const counts = countsOf(rows, saved.rows, options);
  const dirty = hasChanges(request) || counts.length > 0;
  const photos = ((m && m.photos) || []).map((id, i) => ({ value: String(id), label: `Photo ${i + 1}` }));
  const patchOption = (i, p) => setOptions((d) => d.map((o, j) => (j === i ? { ...o, ...p } : o)));
  const patchRow = (key, p) => setRows((d) => d.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const err = (key, field) => errors[`variants.${key}.${field}`];

  const onGenerate = () => {
    const e = optionErrors(options, limits);
    setErrors(e);
    if (Object.keys(e).length) return;
    const out = generate(rows, opts);
    if (out.rows.length > limits.variants) {
      setFormError(`That makes ${out.rows.length} variants; a product has at most ${limits.variants}.`);
      return;
    }
    setFormError('');
    setRows(out.rows);
    toast.info(out.added ? `Added ${out.added} variant${out.added === 1 ? '' : 's'}` : 'Every combination already has a variant');
  };

  const onSave = async () => {
    const e = { ...optionErrors(options, limits), ...rowErrors(rows, opts, limits) };
    if (stale.length) e.variants = `${stale.length} variant${stale.length === 1 ? ' does' : 's do'} not fit the options: change ${stale.length === 1 ? 'its' : 'their'} values or remove ${stale.length === 1 ? 'it' : 'them'}.`;
    setErrors(e);
    setFormError('');
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      if (hasChanges(request)) await save.run(request);
      // New counts of existing lots: the audited stock count, one lot at a time.
      for (const c of counts) await write.update('InventoryUnit', c.unit_id, { qty_on_hand: c.qty_on_hand });
      toast.success('Variants saved');
      query.reload();
    } catch (ex) {
      setErrors(serverErrors(ex, keys));
      setFormError(errorText(ex));
    } finally {
      setBusy(false);
    }
  };

  const remove = (key) => {
    setRows((d) => d.filter((r) => r.key !== key));
    setRemoving(null);
  };
  const removingRow = removing != null ? rows.find((r) => r.key === removing) : null;

  return (
    <SectionCard
      title="Options and variants"
      actions={canEdit && m ? <Button onClick={onSave} loading={busy || save.loading} disabled={!dirty}>Save variants</Button> : null}
    >
      <QueryState query={query}>
        {() => (m ? (
          <div className="space-y-4">
            {!m.options_allowed ? (
              <Alert variant="info">This listing sells one exact unit, so it has one variant. Options (sizes, colours…) are for products sold by quantity.</Alert>
            ) : (
              <>
                <p className="text-sm opacity-70">
                  Sizes, colours and the like (at most {limits.options} options of {limits.values} values each, {limits.variants} variants per product).
                  Each combination is a variant with its own stock, SKU and price.
                </p>
                {errors.options ? <Alert variant="error">{errors.options}</Alert> : null}
                <ol className="space-y-3" aria-label="Options">
                  {options.map((o, i) => (
                    <li key={o.id} className="grid gap-3 rounded-lg border border-[var(--app-border)] p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)_auto]">
                      <Input label="Option" placeholder="Size" value={o.name} maxLength={40} disabled={!canEdit} onChange={(e) => patchOption(i, { name: e.target.value })} error={errors[`options.${i}.name`]} />
                      <Input label="Values" placeholder="S, M, L" helperText="Separate them with commas" value={o.values} disabled={!canEdit} onChange={(e) => patchOption(i, { values: e.target.value })} error={errors[`options.${i}.values`]} />
                      <Select label="Shown as" options={STYLES} value={o.style} disabled={!canEdit} onChange={(v) => v && patchOption(i, { style: String(v) })} closeOnSelect />
                      {canEdit ? (
                        <div className="flex items-end pb-1">
                          <Button size="sm" variant="ghost" aria-label={`Delete option ${o.name || i + 1}`} onClick={() => setOptions((d) => d.filter((_, j) => j !== i))}><Trash2 size={16} /></Button>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ol>
                {canEdit ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" leftIcon={<Plus size={16} />} onClick={() => setOptions((d) => [...d, blankOption()])} disabled={options.length >= limits.options}>Add an option</Button>
                    <Button variant="outline" leftIcon={<Wand2 size={16} />} onClick={onGenerate} disabled={!opts.length}>Generate combinations</Button>
                  </div>
                ) : null}
              </>
            )}

            {m.options_allowed && canEdit && rows.length > 1 ? (
              <div className="flex flex-wrap items-end gap-2 rounded-lg bg-[var(--app-surface)] p-3" aria-label="Bulk edit">
                <div className="w-36"><Input label="Price for all (₹)" value={bulk.price} onChange={(e) => setBulk((b) => ({ ...b, price: e.target.value }))} /></div>
                <div className="w-36"><Input label="Stock for all" value={bulk.qty} onChange={(e) => setBulk((b) => ({ ...b, qty: e.target.value }))} /></div>
                <Button variant="secondary" onClick={() => setRows((d) => applyBulk(d, bulk))} disabled={!bulk.price && !bulk.qty}>Apply to every variant</Button>
              </div>
            ) : null}

            {errors.variants ? <Alert variant="error">{errors.variants}</Alert> : null}
            {formError ? <Alert variant="error">{formError}</Alert> : null}

            <ol className="space-y-3" aria-label="Variants">
              {rows.map((r) => {
                const label = labelOf(r.option_values, opts);
                const isStale = stale.includes(r);
                const lot = r.isNew || isLotLine(r.stock);
                return (
                  <li key={r.key} className="space-y-3 rounded-lg border border-[var(--app-border)] p-3" data-testid={`variant-${label}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{label}</span>
                        {r.default ? <Badge size="sm" variant="secondary">Listing&apos;s own stock</Badge> : null}
                        {r.isNew ? <Badge size="sm" variant="info">New</Badge> : null}
                        {isStale ? <Badge size="sm" variant="warning">Does not fit the options</Badge> : null}
                        {!r.isNew && r.readiness ? <Badge size="sm" variant={r.readiness.ready ? 'success' : 'warning'}>{r.readiness.ready ? 'Ready to sell' : 'Not ready'}</Badge> : null}
                        {err(r.key, 'option_values') ? <span className="text-xs text-error-600">{err(r.key, 'option_values')}</span> : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={r.active} disabled={!canEdit} onChange={(on) => patchRow(r.key, { active: on })} rightLabel="On sale" ariaLabel={`${label} on sale`} />
                        {canEdit && !r.default ? <Button size="sm" variant="ghost" aria-label={`Remove ${label}`} onClick={() => (r.isNew ? remove(r.key) : setRemoving(r.key))}><Trash2 size={16} /></Button> : null}
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
                      <Input label="SKU" value={r.sku} maxLength={64} disabled={!canEdit} onChange={(e) => patchRow(r.key, { sku: e.target.value })} error={err(r.key, 'sku')} />
                      <Input label="Barcode" value={r.barcode} maxLength={64} disabled={!canEdit} onChange={(e) => patchRow(r.key, { barcode: e.target.value })} error={err(r.key, 'barcode')} />
                      <Input label="Price (₹)" placeholder="Listing price" value={r.price} disabled={!canEdit} onChange={(e) => patchRow(r.key, { price: e.target.value })} error={err(r.key, 'price') || err(r.key, 'price_paise')} />
                      <Input label="MRP (₹)" placeholder="Listing MRP" value={r.mrp} disabled={!canEdit} onChange={(e) => patchRow(r.key, { mrp: e.target.value })} error={err(r.key, 'mrp') || err(r.key, 'mrp_paise')} />
                      <Select
                        label="Image"
                        options={[{ value: '', label: 'Listing photos' }, ...photos]}
                        value={r.media_id}
                        disabled={!canEdit || !photos.length}
                        helperText={photos.length ? undefined : 'No approved photos yet'}
                        onChange={(v) => patchRow(r.key, { media_id: v == null ? '' : String(v) })}
                        closeOnSelect
                        error={err(r.key, 'media_id')}
                      />
                      {r.isNew ? (
                        <Input label="Opening stock" value={r.qty} disabled={!canEdit} onChange={(e) => patchRow(r.key, { qty: e.target.value })} error={err(r.key, 'qty') || err(r.key, 'qty_on_hand')} />
                      ) : lot ? (
                        <Input
                          label="Stock count"
                          value={r.count}
                          disabled={!canEdit}
                          helperText={r.stock ? `${r.stock.qty_available} available · ${r.stock.internal_sku}` : undefined}
                          onChange={(e) => patchRow(r.key, { count: e.target.value })}
                          error={err(r.key, 'count')}
                        />
                      ) : (
                        <div className="text-sm"><span className="block font-medium">Stock</span>{r.stock ? `${r.stock.stock_state} · ${r.stock.internal_sku}` : '—'}</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
            <ConfirmDialog
              isOpen={removing != null}
              title={`Remove “${removingRow ? labelOf(removingRow.option_values, opts) : ''}”?`}
              description="When you save, shoppers can no longer buy this variant. Its stock row stays in your inventory."
              confirmLabel="Remove variant"
              destructive
              onConfirm={() => remove(removing)}
              onCancel={() => setRemoving(null)}
            />
          </div>
        ) : null)}
      </QueryState>
    </SectionCard>
  );
}
